import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { MaskedLine, FadeUp } from "./MaskedReveal";

const services = [
  {
    id: "training-c408",
    tag: "01 · Flagship · Insurance-Approved",
    title: "Cessna 408 SkyCourier Training",
    subtitle: "Initial · Recurrent · In-Aircraft Type Training",
    body:
      "El Elyon Air is insurance-approved for in-aircraft C408 SkyCourier training through W. Brown & Associates — the leading aviation insurance underwriter for King Air and turbine aircraft operators. Ground school, systems, procedures, and real-airframe flight training built and delivered by working captains.",
    img: "https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/rlcr17in_IMG_6963.webp",
    span: "md:col-span-12",
    imgH: "h-[440px] md:h-[620px]",
    href: "/training/c408",
    internal: true,
    hero: true,
  },
  {
    id: "training-king-air",
    tag: "02 · Flagship",
    title: "King Air Training",
    subtitle: "200 · 250 · 300 · 350",
    body:
      "Type training for the Beechcraft King Air family — corporate aviation's most-flown turboprop. Owner-flown discipline, single-pilot IFR, and family-difference training.",
    img: "https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/345d7k6y_IMG_7905.webp",
    span: "md:col-span-8",
    imgH: "h-[380px] md:h-[440px]",
    href: "/training/king-air",
    internal: true,
  },
  {
    id: "gov-program",
    tag: "03",
    title: "Government Program Development",
    body:
      "Design and stand-up of aviation programs for public agencies — mission requirements, aircraft-type selection, SOPs, training frameworks, and safety-management systems.",
    img: "https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/zob4fftm_IMG_7911.webp",
    span: "md:col-span-4",
    imgH: "h-[220px]",
    href: "/programs/government",
    internal: true,
  },
  {
    id: "management",
    tag: "04",
    title: "Aircraft Management",
    subtitle: "Professional Oversight · Personalized Service",
    body:
      "Personalized management that protects your investment and simplifies day-to-day operations — scheduling, crew, maintenance, compliance.",
    img: "https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/1w1mf68n_IMG_7235.webp",
    span: "md:col-span-4",
    imgH: "h-[220px]",
    href: "/services/aircraft-management",
    internal: true,
  },
  {
    id: "pilot",
    tag: "05",
    title: "Daily Contract Pilot",
    subtitle: "Ready When You Need Them",
    body:
      "Experienced contract pilots — one day, multi-week, or ongoing — integrated seamlessly into your operation. Part 91 · Part 135. Domestic and international.",
    img: "https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/nci6fdgc_IMG_1247.webp",
    span: "md:col-span-4",
    imgH: "h-[220px]",
    href: "/services/contract-pilot",
    internal: true,
  },
  {
    id: "delivery",
    tag: "06",
    title: "Worldwide Aircraft Delivery",
    subtitle: "Anywhere in the World",
    body:
      "Worldwide aircraft ferry and delivery — flight planning, international regulations, customs coordination, weather analysis. Safe, efficient, ready for the next mission.",
    img: "https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/99608cp4_81FDC356-5790-4500-89B2-2C79DC6CB20C.webp",
    span: "md:col-span-4",
    imgH: "h-[220px]",
    href: "/services/aircraft-delivery",
    internal: true,
  },
  {
    id: "mentoring",
    tag: "07",
    title: "Owner-Flown Mentoring",
    subtitle: "Building Confidence Through Experience",
    body:
      "Mentoring — not classroom training — for owner-pilots. We fly your missions, strengthening decision-making, cockpit management, and confidence. Because confidence is earned one flight at a time.",
    img: "https://customer-assets-wrfwihn1.emergentagent.net/job_elyon-aviation/artifacts/0e670phd_IMG_2833.webp",
    span: "md:col-span-12",
    imgH: "h-[280px] md:h-[360px]",
    href: "/services/owner-mentoring",
    internal: true,
  },
];

const Card = ({ s }) => {
  const inner = (
    <>
      <div className="flex items-start justify-between">
        <span className="font-mono-label text-white/50">{s.tag}</span>
        <span className="text-white/40 group-hover:text-[#FF5A00] transition-colors duration-500">
          ↗
        </span>
      </div>

      <div className="relative mt-6 overflow-hidden corner-frame spotlight-img">
        <div className={`relative ${s.imgH} w-full overflow-hidden`}>
          <img
            src={s.img}
            alt={s.title}
            className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
            loading="lazy"
          />
        </div>
      </div>

      <div className="mt-6">
        <h3 className="font-display text-3xl md:text-4xl lg:text-5xl uppercase tracking-tight leading-[0.95]">
          {s.title}
        </h3>
        {s.subtitle && (
          <p className="mt-2 font-serif-italic text-white/50 text-lg md:text-xl">
            {s.subtitle}
          </p>
        )}
        <p className="mt-4 text-white/60 text-sm md:text-[15px] leading-relaxed max-w-xl">
          {s.body}
        </p>
        {s.internal && (
          <div className="mt-6 font-mono-label text-white/60 group-hover:text-[#FF5A00] transition-colors duration-500">
            View Program →
          </div>
        )}
      </div>
    </>
  );

  const innerClass =
    "group relative w-full h-full border border-white/10 bg-[#0A0A0C] p-6 md:p-8 flex flex-col justify-between overflow-hidden";

  return (
    <motion.div
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0, margin: "0px 0px -10% 0px" }}
      transition={{ duration: 1.1, ease: [0.76, 0, 0.24, 1] }}
      className={`col-span-12 ${s.span}`}
      data-testid={`service-card-${s.id}`}
    >
      {s.internal ? (
        <Link to={s.href} className={innerClass}>
          {inner}
        </Link>
      ) : (
        <a href={s.href} className={innerClass}>
          {inner}
        </a>
      )}
    </motion.div>
  );
};

export const Services = () => {
  return (
    <section
      id="services"
      className="relative bg-[#050505] py-24 md:py-40"
      data-testid="services-section"
    >
      <div className="mx-auto max-w-[1920px] px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="md:col-span-5">
            <FadeUp>
              <div className="font-mono-label text-white/50 mb-6">
                <span className="text-[#FF5A00]">◉</span> Services · 001 — 007
              </div>
            </FadeUp>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl uppercase leading-[0.9] tracking-tight">
              <MaskedLine>The Practice</MaskedLine>
              <MaskedLine delay={0.15} className="font-serif-italic italic text-white/70">
                of flight.
              </MaskedLine>
            </h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <FadeUp delay={0.2}>
              <p className="text-white/60 text-base md:text-lg leading-relaxed max-w-lg">
                Seven disciplines, one standard. From the first ground-school
                hour to the last mile of a transatlantic delivery — every step
                is engineered around precision, safety, and the quiet
                confidence of a well-flown airplane.
              </p>
            </FadeUp>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 md:gap-8">
          {services.map((s) => (
            <Card key={s.id} s={s} />
          ))}
        </div>
      </div>
    </section>
  );
};
